import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg } from "common-fe";
import useReviewQueue from "../hooks/useReviewQueue";
import useFvdPushFailures from "../hooks/useFvdPushFailures";
import AttachRideDialog from "../components/AttachRideDialog";
import RideLink, { formatRideLabel } from "../components/RideLink";
import ClassLink from "../components/ClassLink";
import FvdPushFailureCauseChip from "../components/FvdPushFailureCauseChip";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { classNumberLabel, formatVenueLocalDateTime } from "../util/classDisplay";
import { parseFvdPushError } from "../util/fvdPushError";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { hasResolveStaleWriteConflictPermission } from "../permissions";

// #3009 - a conflicted field's value for display: `null`/`undefined` (the
// field was never set) renders as a plain placeholder rather than a blank
// cell or the literal string "null" (a plain String() would also mangle a
// nested/array value, hence the JSON.stringify branch for objects).
function formatConflictValue(value) {
  if (value === null || value === undefined) return "(none)";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// #3009 - resolveStaleWriteConflict can fail with five specific codes
// (concurrent_modification, sheet_completed, validation_error, and, judge
// round 2 D2, marks_confirmed_frozen / elimination_change_needs_sheet_edit
// on apply_attempted) on top of the generic no_conflict/invalid_action
// shapes every other resolve already covers. Each gets its own message so
// score_admin knows WHY the resolve was refused and what to do instead
// (usually: Keep current value here, fix it on the sheet itself), not just
// that it was. A plain lookup table of static key strings
// (never `t(\`...${code}\`)`) -- i18n keys must stay static so the
// extraction tooling can see every string (memory: feedback_no_dynamic_i18n_keys.md).
const STALE_WRITE_RESOLVE_ERROR_I18N_KEY = {
  concurrent_modification: "reviewQueue.other.staleWriteConflict.resolveErrors.concurrentModification",
  sheet_completed: "reviewQueue.other.staleWriteConflict.resolveErrors.sheetCompleted",
  validation_error: "reviewQueue.other.staleWriteConflict.resolveErrors.validationError",
  marks_confirmed_frozen: "reviewQueue.other.staleWriteConflict.resolveErrors.marksConfirmedFrozen",
  elimination_change_needs_sheet_edit:
    "reviewQueue.other.staleWriteConflict.resolveErrors.eliminationChangeNeedsSheetEdit",
};

function staleWriteResolveErrorKey(code) {
  return STALE_WRITE_RESOLVE_ERROR_I18N_KEY[code] || "reviewQueue.other.staleWriteConflict.resolveErrors.default";
}

// #2813 (scribe-resilience workstream, gap G6) — admin half of the
// generalised review queue: subject/reason/decision/options/provenance/
// resolution, not a bare ride row. The manual-rides table below still
// renders ride-subject items with the same RideLink/ClassLink shape #387
// always used; its Actions column is now DERIVED from item.options rather
// than a hardcoded "Attach" button (AC3) — "attach" opens the existing
// AttachRideDialog (it needs an operator-chosen target + the test-mismatch
// guard), any other action calls the generic resolve endpoint directly.
// Non-ride items (today: quarantined payloads, #2812) render in their own
// section below, fully generic — decision text plus buttons from options.
export default function ReviewQueue() {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  useDocumentTitle(t("title.reviewQueue"));
  const { items, loading, error, refetch, resolveItem } = useReviewQueue();
  const {
    rides: fvdFailureRides,
    loading: fvdFailuresLoading,
    error: fvdFailuresError,
  } = useFvdPushFailures();
  const scoreApi = useScoreApi();
  const [selectedRide, setSelectedRide] = useState(null);
  const [search, setSearch] = useState("");
  const [resolveError, setResolveError] = useState(null);

  // #3009 - does this user hold resolve_stale_write_conflict for the active
  // org? Same literal-roles-claim predicate RideEditor.jsx's
  // canSwitchAuthoritative already uses for switch_authoritative_score_sheet
  // (permissions.js's own header explains why: no common-fe usePermission(),
  // account-api emits no `permissions` claim). Drives whether the stale_write
  // row below shows its two resolve buttons at all - a scribe-only token
  // never even sees them.
  const auth = useAuth();
  const { activeOrgId } = useActiveOrg();
  const canResolveStaleWriteConflict = useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken || !activeOrgId) return false;
    try {
      const claims = jwtDecode(accessToken);
      const raw = claims.roles;
      const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      return hasResolveStaleWriteConflictPermission(map[activeOrgId]);
    } catch {
      return false;
    }
  }, [auth.user?.access_token, activeOrgId]);

  const rideItems = useMemo(() => items.filter((item) => item.subject?.type === "ride"), [items]);
  const otherItems = useMemo(() => items.filter((item) => item.subject?.type !== "ride"), [items]);

  // A single generic dispatch, shared by every section: "attach" always
  // means "needs an operator-chosen target", regardless of reason, so it
  // opens the picker; every other action code resolves directly. This is
  // the ONLY place that branches on an action name — nothing here branches
  // on `reason`.
  const handleOption = async (item, option) => {
    if (option.action === "attach") {
      setSelectedRide(item.subject);
      return;
    }
    // #2927 — "sign" deep-links into the existing Complete & Sign flow
    // (RideEditor, /rides/:rideId) rather than resolving generically: this
    // subject type has no generic resolution (lib/review-queue.js's
    // confirmed_unsigned_sheet catalog entry), it just drops out of the next
    // GET /review-queue once the real completeScoreSheet endpoint stamps
    // completedAt. AC2 (judge round 1) — the ride can carry 2+ sheets (one
    // per judge), and RideEditor keeps its own activeSheetId independent of
    // the URL, so a bare /rides/:rideId lands on whichever sheet loads
    // first, not necessarily the unsigned one this item is about. The
    // `?sheet=` query param carries item.subject.id (the sheet id) so
    // RideEditor can open the right tab directly.
    if (option.action === "sign") {
      navigate(`/rides/${item.subject.rideId}?sheet=${item.subject.id}`);
      return;
    }
    // #3009 - a stale_write item resolves through its OWN dedicated,
    // permission-gated endpoint (POST /scoresheet/:id/resolve-stale-write-conflict),
    // never the generic /review-queue/:itemId/resolve every other action here
    // uses - lib/review-queue.js's stale_write REASON_CATALOG comment names
    // this as the reason a same-field conflict needs its own route at all
    // (the generic route has no config.requirePermission to gate on).
    if (item.subject?.type === "stale_write") {
      setResolveError(null);
      try {
        unwrap(await scoreApi.scoreSheets.resolveStaleWriteConflict(item.subject.id, { action: option.action }));
        await refetch();
      } catch (err) {
        setResolveError({ i18nKey: staleWriteResolveErrorKey(err?.response?.data?.code) });
        // #3009 - refresh even on a refused resolve: concurrent_modification
        // and sheet_completed both mean this item's server-side state moved
        // since the row was loaded (another write landed, or the sheet was
        // signed), so the stale row on screen must be replaced with the
        // real current state rather than left showing pre-failure data.
        await refetch();
      }
      return;
    }
    setResolveError(null);
    try {
      await resolveItem(item.id, option.action);
    } catch {
      setResolveError({ i18nKey: "reviewQueue.resolveError" });
    }
  };

  // #869 — search over CNO/rider/horse/class, matching the ClassManager /
  // PeopleManager / HorseManager filtered-list pattern. Class text prefers
  // the resolved classNumberLabel (#864's join) so a search matches the
  // visible label, falling back to the raw classId when the lookup missed.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rideItems;
    return rideItems.filter((item) => {
      const ride = item.subject;
      return [
        ride.competitorNumber,
        ride.riderName,
        ride.horseName,
        ride.class ? classNumberLabel(ride.class) : ride.classId,
      ].some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [rideItems, search]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("reviewQueue.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.subtitle")}
      </Typography>

      {resolveError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="review-item-resolve-error">
          {t(resolveError.i18nKey)}
        </Alert>
      )}

      <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
        {t("reviewQueue.manualSection.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("reviewQueue.loadError")}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton rows={4} columns={3} />
      ) : rideItems.length === 0 ? (
        <Typography color="text.secondary">{t("reviewQueue.empty")}</Typography>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            sx={{ mb: 2, maxWidth: 360 }}
            placeholder={t("reviewQueue.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {filtered.length === 0 ? (
            <Typography color="text.secondary">{t("reviewQueue.noResults")}</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reviewQueue.columns.ride")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.class")}</TableCell>
                    <TableCell align="right">{t("reviewQueue.columns.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item) => {
                    const ride = item.subject;
                    const id = ride.id ?? ride.rideId;
                    // #865 — surface a failed lookup as a placeholder, not the
                    // raw id; the id still reaches support via the tooltip.
                    const classLookupMissed = !!ride.classId && !ride.class;
                    return (
                      <TableRow key={id} hover>
                        <TableCell>
                          {/* #860 — shared RideLink ("#CNO, Rider on Horse"),
                              linked into the RideEditor route. Supersedes the
                              separate CNO/horse/rider columns; this row was
                              previously not linked at all. */}
                          <RideLink ride={ride} rideId={id} />
                        </TableCell>
                        <TableCell>
                          {/* #864 — shared ClassLink; ride.class is the resolved
                              class object from useReviewQueue's best-effort lookup
                              (null on a failed lookup — link falls back to the
                              placeholder below). */}
                          <Tooltip title={classLookupMissed ? ride.classId : ""}>
                            <span>
                              <ClassLink
                                classObj={ride.class}
                                classId={ride.classId}
                                fallback={t("common.unknownClass")}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {/* #2813 AC3 — buttons derived from item.options,
                              never a hardcoded action per reason. */}
                          {item.options.map((option, index) => (
                            <Button
                              key={option.action}
                              size="small"
                              variant="outlined"
                              sx={{ color: "text.primary", borderColor: "text.primary", ml: index > 0 ? 1 : 0 }}
                              onClick={() => handleOption(item, option)}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Card #1608 — its OWN section, deliberately not merged into the
          manual-review table above: the operator action differs (unlock/retry
          an FVD write vs. finish/verify a manual entry), and the columns
          differ (cause/attempts/raw error vs. none of that). Driver decision
          recorded on the card. */}
      <Typography variant="h6" color="text.primary" sx={{ mt: 4, mb: 1 }}>
        {t("reviewQueue.fvdFailures.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.fvdFailures.subtitle")}
      </Typography>

      {fvdFailuresError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fvdFailuresError.message || t("reviewQueue.fvdFailures.loadError")}
        </Alert>
      )}

      {fvdFailuresLoading ? (
        <TableSkeleton rows={4} columns={5} />
      ) : fvdFailureRides.length === 0 ? (
        <Typography color="text.secondary">{t("reviewQueue.fvdFailures.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("reviewQueue.fvdFailures.columns.ride")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.class")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.cause")}</TableCell>
                <TableCell align="right">{t("reviewQueue.fvdFailures.columns.attempts")}</TableCell>
                <TableCell>{t("reviewQueue.fvdFailures.columns.detail")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fvdFailureRides.map((ride) => {
                const id = ride.id ?? ride.rideId;
                const classLookupMissed = !!ride.classId && !ride.class;
                const { cause, attempts, rawError } = parseFvdPushError(ride.fvdPushError);
                const pushedAt = ride.fvdPushedAt ? formatVenueLocalDateTime(ride.fvdPushedAt) : null;
                return (
                  <TableRow key={id} hover data-testid="fvd-push-failure-row">
                    <TableCell>
                      <RideLink ride={ride} rideId={id} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={classLookupMissed ? ride.classId : ""}>
                        <span>
                          <ClassLink
                            classObj={ride.class}
                            classId={ride.classId}
                            fallback={t("common.unknownClass")}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={pushedAt ? t("reviewQueue.fvdFailures.pushedAtTooltip", { when: pushedAt }) : ""}>
                        <span>
                          <FvdPushFailureCauseChip cause={cause} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" data-testid="fvd-push-failure-attempts">
                      {attempts ?? t("reviewQueue.fvdFailures.attemptsUnknown")}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={rawError || ""}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid="fvd-push-failure-detail"
                          sx={{
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rawError || t("reviewQueue.fvdFailures.noDetail")}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* #2813 — every OTHER review-item subject (today: quarantined
          payloads, #2812), rendered fully generically: decision text plus
          buttons from item.options. Nothing here branches on `reason` — a
          reason this page has never seen before still renders correctly as
          long as score-api's REASON_CATALOG (lib/review-queue.js) knows it. */}
      <Typography variant="h6" color="text.primary" sx={{ mt: 4, mb: 1 }}>
        {t("reviewQueue.other.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.other.subtitle")}
      </Typography>

      {loading ? (
        <TableSkeleton rows={2} columns={3} />
      ) : otherItems.length === 0 ? (
        <Typography color="text.secondary">{t("reviewQueue.other.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("reviewQueue.other.columns.reason")}</TableCell>
                <TableCell>{t("reviewQueue.other.columns.subject")}</TableCell>
                <TableCell>{t("reviewQueue.other.columns.decision")}</TableCell>
                <TableCell align="right">{t("reviewQueue.other.columns.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {otherItems.map((item) => {
                // #2927 (card "Do" item 2) — a confirmed_unsigned_sheet row
                // carries enough subject detail (competitorNumber, riderName,
                // horseName, judgeName) to identify the sheet without a
                // click-through; every other reason this section has ever
                // seen (quarantined_payload, a synthetic/unrecognised reason
                // in the AC3 test) has no such fields, so this column stays
                // blank for them rather than guessing at a shape.
                const isConfirmedUnsignedSheet = item.subject?.type === "confirmed_unsigned_sheet";
                // #3009 - a stale_write row: same "identify the sheet without
                // a click-through" posture as confirmed_unsigned_sheet above,
                // plus its own conflicting-field detail (current vs attempted
                // value per field) so score_admin can decide without leaving
                // this page. Its two resolve buttons below are gated on
                // canResolveStaleWriteConflict - a scribe still sees the row
                // and the conflict detail, just never the buttons (AC2).
                const isStaleWriteConflict = item.subject?.type === "stale_write";
                return (
                <TableRow key={item.id} hover data-testid="review-item-row">
                  <TableCell>
                    {t(`reviewQueue.other.reasons.${item.reason}`, { defaultValue: item.reason })}
                  </TableCell>
                  <TableCell data-testid="review-item-subject">
                    {isConfirmedUnsignedSheet && (
                      <>
                        <Typography variant="body2" color="text.primary">
                          {formatRideLabel(item.subject, t("rideEditor.onHorse"))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("reviewQueue.other.judgeLabel", {
                            name: item.subject.judgeName || t("common.unknownJudge"),
                          })}
                        </Typography>
                      </>
                    )}
                    {isStaleWriteConflict && (
                      <>
                        <Typography variant="body2" color="text.primary">
                          {formatRideLabel(item.subject, t("rideEditor.onHorse"))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("reviewQueue.other.judgeLabel", {
                            name: item.subject.judgeName || t("common.unknownJudge"),
                          })}
                        </Typography>
                        {(item.subject.conflictFields || []).map((field) => (
                          <Typography
                            key={field}
                            variant="body2"
                            color="text.secondary"
                            data-testid="stale-write-conflict-field"
                          >
                            {t("reviewQueue.other.staleWriteConflict.fieldRow", {
                              field,
                              current: formatConflictValue(item.subject.current?.[field]),
                              attempted: formatConflictValue(item.subject.attempted?.[field]),
                            })}
                          </Typography>
                        ))}
                      </>
                    )}
                  </TableCell>
                  <TableCell>{item.decision}</TableCell>
                  <TableCell align="right">
                    {isStaleWriteConflict && !canResolveStaleWriteConflict
                      ? null
                      : item.options.map((option, index) => (
                          <Button
                            key={option.action}
                            size="small"
                            variant="outlined"
                            sx={{ color: "text.primary", borderColor: "text.primary", ml: index > 0 ? 1 : 0 }}
                            onClick={() => handleOption(item, option)}
                          >
                            {option.label}
                          </Button>
                        ))}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AttachRideDialog
        open={!!selectedRide}
        manualRide={selectedRide}
        onClose={() => setSelectedRide(null)}
        onResolved={refetch}
      />
    </Box>
  );
}
